import axios from "axios";
import { hostInfo } from "@/lib/brand";

// Portal base path. On the dedicated Docstate client domain the portal is served
// at the root ("/buy", "/property/x"); everywhere else it lives under "/docstate".
export const DS_BASE = (hostInfo().brand === "docstate" && hostInfo().type === "client") ? "" : "/docstate";

// Build an in-portal link that respects DS_BASE. dl() -> portal home.
export const dl = (p = "") => {
  const s = p && !String(p).startsWith("/") ? "/" + p : p;
  return (DS_BASE + (s || "")) || "/";
};

// Build a human-readable enquiry source from UTM params or the referrer.
export const enquirySource = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    const campaign = p.get("utm_campaign");
    const src = p.get("utm_source");
    if (campaign || src) return [src, campaign].filter(Boolean).join(" / ");
    const ref = document.referrer;
    if (ref) {
      const h = new URL(ref).hostname.replace(/^www\./, "");
      if (h && !window.location.hostname.includes(h)) return h;
    }
    return "Direct";
  } catch { return "Direct"; }
};

// Public Docstate portal API — a clean client (no admin auth/brand headers).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API });

export const reContent = () => client.get("/realestate/content").then((r) => r.data);
export const reListings = (params) => {
  // drop empty values so empty number params don't 422 on the backend
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== "" && v != null));
  return client.get("/realestate/listings", { params: clean }).then((r) => r.data);
};
export const reDetail = (id) => client.get(`/realestate/listings/${id}`).then((r) => r.data);
export const reAgents = () => client.get("/realestate/agents").then((r) => r.data);
export const submitViewing = (body) => client.post("/realestate/viewings", body).then((r) => r.data);
export const submitBroker = (body) => client.post("/realestate/brokers", body).then((r) => r.data);
export const submitEnquiry = (body) => client.post("/realestate/enquiries", body).then((r) => r.data);
export const reChat = (session_id, message) => client.post("/realestate/chat", { session_id, message }).then((r) => r.data);
export const rePage = (slug) => client.get(`/realestate/pages/${slug}`).then((r) => r.data);
export const rePosts = (params) => client.get("/realestate/posts", { params }).then((r) => r.data);
export const rePost = (slug) => client.get(`/realestate/posts/${slug}`).then((r) => r.data);
export const reGuides = () => client.get("/realestate/guides").then((r) => r.data);
export const reGuide = (slug) => client.get(`/realestate/guides/${slug}`).then((r) => r.data);
export const reCareers = () => client.get("/realestate/careers").then((r) => r.data);
export const reMedia = (kind) => client.get("/realestate/media", { params: kind ? { kind } : {} }).then((r) => r.data);

export const aed = (n, period) => {
  const base = "AED " + Number(n || 0).toLocaleString("en-AE");
  if (!period) return base;
  const p = period === "yearly" ? "/yr" : period === "monthly" ? "/mo" : `/${period}`;
  return base + p;
};

export const PROPERTY_TYPES = ["apartment", "villa", "townhouse", "penthouse", "duplex", "studio", "plot", "office", "retail", "warehouse"];
export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// Canonical public URL for a listing — safe to share anywhere.
export const propertyUrl = (listing) => {
  const ref = listing.reference || listing.id;
  return `${window.location.origin}/docstate/property/${ref}`;
};

// A ready-to-send WhatsApp "share this unit" link. Opens WhatsApp with a
// professional pre-filled message (title, price, ref + public link) so the
// customer can save/wishlist it. No recipient set → forwards to any contact.
export const waShareUrl = (listing) => {
  const beds = listing.type === "studio" ? "Studio" : `${listing.bedrooms || ""} Bed`.trim();
  const lines = [
    `*${listing.title}*`,
    `${aed(listing.price, listing.purpose === "rent" ? listing.price_period : "")}${listing.community ? ` · ${listing.community}` : ""}`,
    [beds, listing.bathrooms ? `${listing.bathrooms} Bath` : "", listing.area_sqft ? `${Number(listing.area_sqft).toLocaleString()} sqft` : ""].filter(Boolean).join(" · "),
    listing.reference ? `Ref: ${listing.reference}` : "",
    "",
    `View & save: ${propertyUrl(listing)}`,
    "",
    "Shared by Docstate Properties — the doctor of Dubai real estate.",
  ].filter((l) => l !== undefined);
  return `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
};

// Inject/replace a page-level JSON-LD block (AEO/SEO). Cleans up on unmount.
export const setJsonLd = (id, obj) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(obj);
};
export const clearJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};
